// Everything the app proposed on a day, read back as one list.
//
// A proposal is a ticket the pad printed or the day's ranking put at the top,
// whether or not anyone placed it. Sets are what `store.js` keeps in
// `prefs.generations` (every set dealt, and — from wave 3 — the live pad saved
// before the board changes under it); picks are what `recordPicks` below keeps
// in `prefs.picks`. The paper trail settles this list the next morning, and the
// evidence a strategy shows on its card is counted from it.
//
// Pure: reads prefs, touches no network, so it runs in node tests. The
// contract below is what `paper-trail.js` is written against — keep the field
// names when extending this.

import { prefs } from '../core/storage.js';
// A leaf with no imports of its own, so writing the trail cannot drag the
// store — or a browser — into a node test.
import { stampLegs } from './sets.js';

/** Where the two halves of the trail live in prefs, both keyed by 'YYYY-MM-DD'. */
export const GENERATIONS_KEY = 'generations';
export const PICKS_KEY = 'picks';

/**
 * And the third: the legs the gate REFUSED, kept beside the ones it printed.
 *
 * A separate key rather than a `status: 'rejected'` row inside `picks`, and the
 * reason is containment. Everything downstream of `proposalsFor` — the day's
 * summary, a strategy's evidence, the scoreboard, the funnel, the rollup, the
 * feed — counts a proposal as something the app said it would bet. A refusal is
 * the opposite claim, and one consumer forgetting to filter it out would quietly
 * credit a strategy with legs it explicitly declined. Same store, same pruning,
 * same settle pass; its own drawer.
 */
export const REJECTIONS_KEY = 'rejections';

/** How many days of them are worth keeping on the device. */
export const KEEP_DAYS = 60;

/**
 * The legs' identity for de-duplication: the same strategy printing the same
 * legs at the same prices in two sets is one proposal, not two.
 */
export function proposalKey(strategyId, legs) {
  const parts = (legs ?? []).map((l) => `${l.matchKey ?? l.eventId}:${l.side}@${l.price}`);
  return `${strategyId}|${parts.join(',')}`;
}

/**
 * proposalsFor(date) → [{
 *   id,            stable: `${date}:${source}:${multi.id}`
 *   date,          'YYYY-MM-DD' (the pad's date)
 *   source,        'set' | 'pick'
 *   setIndex,      position in the day's sets (sets only), 0-based
 *   rank,          1..4 (picks only)
 *   superseded,    picks only: proposed, then out-ranked later the same day
 *   strategyId, strategyName,
 *   multi,         the ticket header as stored ({ id, combinedOdds, stake, tier?, label, strategyId, ... })
 *   legs,          [{ matchKey, side, price, selection, match, sport, kickoff, eventId? }]
 *   pricesFrom,    ISO time the prices were fetched, when known
 * }]
 * Sets first in dealt order, then picks; duplicates (same strategy, same legs
 * at the same prices) keep the first occurrence.
 */
export function proposalsFor(date) {
  const out = [];
  const seen = new Set();
  const push = (p) => {
    const key = proposalKey(p.strategyId, p.legs);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(p);
  };

  let sets = [];
  let picks = [];
  try { sets = prefs[GENERATIONS_KEY]?.[date] ?? []; } catch { sets = []; }
  try { picks = prefs[PICKS_KEY]?.[date] ?? []; } catch { picks = []; }

  (Array.isArray(sets) ? sets : []).forEach((set, setIndex) => {
    for (const t of set?.tickets ?? []) {
      if (!t?.multi || !t.legs?.length) continue;
      push({
        id: `${date}:set:${t.multi.id}`,
        date,
        source: 'set',
        setIndex,
        strategyId: t.multi.strategyId ?? 'classic-four-ways',
        strategyName: t.multi.label ?? 'Classic Four Ways',
        multi: t.multi,
        legs: t.legs,
        pricesFrom: set.pricesFrom ?? t.multi.oddsFetchedAt ?? null,
      });
    }
  });

  (Array.isArray(picks) ? picks : []).forEach((p) => {
    if (!p?.multi || !p.legs?.length) return;
    push({
      id: `${date}:pick:${p.multi.id}`,
      date,
      source: 'pick',
      rank: p.rank ?? null,
      // Picks only: this one was proposed and then out-ranked by a later
      // reading of the same day. It was still proposed, so it is still on the
      // trail — a strategy's evidence has to count what it said, not only what
      // it was still saying by the end of the day.
      superseded: p.superseded === true,
      strategyId: p.strategyId ?? p.multi.strategyId,
      strategyName: p.strategyName ?? p.multi.label,
      multi: p.multi,
      legs: p.legs,
      pricesFrom: p.pricesFrom ?? p.multi.oddsFetchedAt ?? null,
    });
  });

  return out;
}

/**
 * The day's picks list after a fresh ranking: the new picks, then anything the
 * old list proposed that the new one no longer does, carried forward with
 * `superseded: true`.
 *
 * The ranking moves as the prices do, and the latest reading is the one that
 * stands — but a pick proposed this morning and out-ranked by lunchtime was
 * still proposed, and the trail has to settle it. Identity is `proposalKey`,
 * the legs at their prices, so re-opening the screen on the same board
 * rewrites the same list instead of growing it.
 */
export function mergePicks(previous, fresh) {
  const out = [...(Array.isArray(fresh) ? fresh : [])];
  const seen = new Set(out.map((p) => proposalKey(p?.strategyId, p?.legs)));
  for (const old of (Array.isArray(previous) ? previous : [])) {
    if (!old) continue;
    const key = proposalKey(old.strategyId, old.legs);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...old, superseded: true });
  }
  return out;
}

/**
 * Write the day's picks to the paper trail. Answers the day's list as it now
 * stands, or null when there was nothing to write.
 *
 * A pick is a proposal whether or not anybody backed it, so it is kept with the
 * prices it was ranked at — `proposalsFor` reads this list beside the day's
 * sets, and tomorrow morning the trail settles both.
 *
 * `byKey` is the board's `matchIndex`, and the legs are stamped against it HERE
 * rather than later: a pick kept carrying only a provider event id is one the
 * trail cannot settle, and by tomorrow the board that could have answered is
 * gone. `pricesFrom` is when those prices were fetched, `at` when the ranking
 * was taken.
 *
 * The day's entry is REPLACED rather than appended to — the ranking moves as
 * the prices do and the latest reading is the one that stands — but nothing it
 * used to hold is lost: `mergePicks` carries an out-ranked pick forward as
 * superseded, and that is where the rule is written down and tested.
 *
 * No demo guard here. /demo writes nothing, and the caller is the one that
 * knows it is /demo — this file's job is the shape of the trail.
 */
export function recordPicks(date, list, { pricesFrom = null, byKey, at = new Date().toISOString() } = {}) {
  const fresh = (list ?? []).map((p) => ({
    rank: p.rank ?? null,
    strategyId: p.strategy?.id ?? p.multi?.strategyId ?? null,
    strategyName: p.strategy?.name ?? p.multi?.label ?? null,
    multi: { ...p.multi },
    legs: stampLegs(p.legs, byKey),
    valueVsMarket: p.valueVsMarket ?? null,
    pricesFrom,
    at,
  }));

  const stored = prefs[PICKS_KEY];
  const all = stored && typeof stored === 'object' ? stored : {};
  const previous = Array.isArray(all[date]) ? all[date] : [];
  // Nothing ranked and nothing ever ranked: an empty day is not worth a row,
  // and writing one would rewrite the whole store on every quiet morning.
  if (!fresh.length && !previous.length) return null;

  const day = mergePicks(previous, fresh);
  prefs[PICKS_KEY] = { ...all, [date]: day };
  return day;
}

/**
 * The legs the gate refused on a day, as rows the settle pass can settle.
 *
 * Read the same way `proposalsFor` reads its two halves, and returning the same
 * shape, so `settleProposal` needs to know nothing about what a refusal is.
 */
export function rejectionsFor(date) {
  let list = [];
  try { list = prefs[REJECTIONS_KEY]?.[date] ?? []; } catch { list = []; }
  return (Array.isArray(list) ? list : []).filter((r) => r?.multi && r.legs?.length);
}

/**
 * Write the day's refusals. Answers the list as stored, or null when there was
 * nothing to write and nothing already there.
 *
 * REPLACED RATHER THAN APPENDED TO, exactly as `recordPicks` replaces the day's
 * picks: the pad is a computed over the board, so a re-read of the same morning
 * is the same refusals said again, not more of them. `rejectionsFrom` has
 * already capped and ordered the list; this only stores it.
 */
export function recordRejections(date, list) {
  const fresh = Array.isArray(list) ? list : [];
  let stored;
  try { stored = prefs[REJECTIONS_KEY]; } catch { stored = null; }
  const all = stored && typeof stored === 'object' ? stored : {};
  const previous = Array.isArray(all[date]) ? all[date] : [];
  // A quiet morning that has always been quiet is not worth a row, and writing
  // one would rewrite the whole store every time the board is re-read.
  if (!fresh.length && !previous.length) return null;
  try { prefs[REJECTIONS_KEY] = { ...all, [date]: fresh }; } catch { /* private mode */ }
  return fresh;
}

/**
 * Take one ticket off a day's trail, by `multi.id`. Answers how many rows went.
 *
 * THE ONLY THING THAT MAY BE FORGOTTEN IS A TICKET NOBODY BUT THE PUNTER
 * PROPOSED. A strategy's proposal is evidence about that strategy and the trail
 * has to keep it whether or not anybody fancied it — see `store.js`'s
 * `deleteBuilt`, which is the only caller and which refuses anything the engine
 * dealt. This function does not know what a built ticket is; the caller does,
 * and this file's job is the shape of the trail.
 *
 * Both halves are swept, because either could hold the id: `recordPicks` writes
 * a built ticket into `picks`, and a set that somehow carried one would be the
 * same row said twice. A day left with nothing is dropped rather than stored
 * empty.
 */
export function forgetProposal(date, multiId) {
  if (!date || !multiId) return 0;
  let gone = 0;

  // Picks: the row IS the ticket.
  gone += rewriteDay(PICKS_KEY, date, (day) => day.filter((p) => p?.multi?.id !== multiId));

  // Sets: the row is a pad, and a pad left with no tickets on it is not a set.
  gone += rewriteDay(GENERATIONS_KEY, date, (day) => day
    .map((set) => {
      const tickets = (set?.tickets ?? []).filter((t) => t?.multi?.id !== multiId);
      return tickets.length === (set?.tickets ?? []).length ? set : { ...set, tickets };
    })
    .filter((set) => (set?.tickets ?? []).length > 0));

  return gone;
}

/**
 * `prefs[key][date]` through a function, written back only when it changed.
 * Answers how many rows the day lost — a day left empty is dropped, not stored.
 */
function rewriteDay(key, date, fn) {
  let all;
  try { all = prefs[key]; } catch { return 0; }
  if (!all || typeof all !== 'object' || !Array.isArray(all[date])) return 0;

  const before = all[date];
  const after = fn(before);
  const lost = countTickets(before) - countTickets(after);
  if (lost === 0) return 0;

  const next = { ...all };
  if (after.length) next[date] = after;
  else delete next[date];
  try { prefs[key] = next; } catch { /* private mode */ }
  return lost;
}

/** Tickets in a day of either store: a pick is one, a set is however many. */
const countTickets = (day) => (day ?? []).reduce(
  (n, row) => n + (Array.isArray(row?.tickets) ? row.tickets.length : 1),
  0,
);

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 'YYYY-MM-DD' plus n calendar days. String arithmetic, the same reason
 * `store.js` does it: adding 24 hours to a bare date lands on the wrong day
 * either side of a daylight-saving change.
 */
function shiftDate(date, n) {
  const [y, m, d] = String(date).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * The day-keyed store (`prefs.generations` or `prefs.picks`) with everything
 * older than `keepDays` dropped: `{ '2026-09-13': [...] }` in, the same shape
 * out. Pure — the caller writes the result back.
 *
 * Both stores live in localStorage, which is a few megabytes shared with
 * everything else the app keeps, and a day nobody will ever settle again is
 * not worth the room. The cutoff is inclusive: with 60 days kept, the day 60
 * days ago stays and the day before it goes.
 *
 * A key that is not a date is left alone. Whatever it is, this is not the code
 * that gets to decide it is rubbish.
 */
export function pruneOld(prefsObj, keepDays = KEEP_DAYS, today = new Date().toISOString().slice(0, 10)) {
  if (!prefsObj || typeof prefsObj !== 'object') return {};
  if (!ISO_DAY.test(String(today))) return { ...prefsObj };
  const cutoff = shiftDate(today, -Math.max(0, Number(keepDays) || 0));
  const kept = {};
  for (const [date, value] of Object.entries(prefsObj)) {
    if (ISO_DAY.test(date) && date < cutoff) continue;
    kept[date] = value;
  }
  return kept;
}
